import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Legend, Tooltip as ReTooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { Trophy, Medal, Award, FileText, ExternalLink, RefreshCw, Loader2, Download, Map as MapIcon } from 'lucide-react';
import { MapContainer, TileLayer, CircleMarker, Popup, Tooltip as MapTooltip } from 'react-leaflet';
import api from '@/lib/api';

const INDEX_LABELS = {
  demographic: 'Demográfico',
  demand: 'Demanda',
  commercial_intensity: 'Intensidad',
  competition: 'Competencia',
  complementarity: 'Complement.',
  accessibility: 'Accesibilidad',
  dynamism: 'Dinamismo',
  tourism: 'Turismo',
  financial: 'Financiero',
  information_quality: 'Info',
  risk: 'Riesgo',
};

const PALETTE = ['#1e3a8a', '#0f766e', '#b45309', '#7c3aed', '#be123c', '#0891b2', '#65a30d', '#c2410c', '#4338ca', '#dc2626'];

const scoreColor = (s) => {
  if (s == null) return '#94a3b8';
  if (s >= 75) return '#15803d';
  if (s >= 55) return '#1e3a8a';
  if (s >= 40) return '#b45309';
  return '#b91c1c';
};

export default function BatchComparison() {
  const { batchId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    api.get(`/analyses/batch/${batchId}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    const t = setInterval(() => {
      api.get(`/analyses/batch/${batchId}`).then((r) => {
        setData(r.data);
        const running = r.data.analyses.some((a) => !['completado', 'completado_con_advertencias', 'fallido'].includes(a.status));
        if (!running) clearInterval(t);
      });
    }, 8000);
    return () => clearInterval(t);
  }, [batchId]);

  const analyses = useMemo(() => (data?.analyses || []).slice().sort((a, b) => (b.score_general || 0) - (a.score_general || 0)), [data]);
  const completed = analyses.filter((a) => a.score_general != null);
  const running = analyses.length - completed.length;
  const avgScore = completed.length ? completed.reduce((s, a) => s + a.score_general, 0) / completed.length : null;

  const downloadBatchPdf = async () => {
    try {
      const token = localStorage.getItem('strategia_token');
      const r = await fetch(`${api.defaults.baseURL}/analyses/batch/${batchId}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error('Error PDF');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.download = `StrategIA_Batch_${(data?.batch_name || batchId).replace(/\s+/g, '_')}.pdf`;
      link.click();
      toast.success('PDF consolidado descargado');
    } catch (e) {
      toast.error('Error al descargar PDF');
    }
  };

  // Data for the radar overlay (top 5 by score)
  const top5 = completed.slice(0, 5);
  const radarData = Object.keys(INDEX_LABELS).filter((k) => k !== 'risk').map((k) => {
    const row = { subject: INDEX_LABELS[k] };
    top5.forEach((a, i) => { row[a.location.municipality_names[0] || `#${i + 1}`] = a.indices?.[k]?.score ?? 0; });
    return row;
  });

  // Data for the bar chart (score comparison)
  const barData = analyses.map((a) => ({
    name: a.location.municipality_names[0] || a.name,
    score: a.score_general || 0,
    category: a.score_category || '—',
  }));

  if (loading || !data) return <div className="p-12 text-slate-500">Cargando comparación…</div>;

  return (
    <div className="p-6 md:p-10 max-w-[1400px]">
      <div className="label-caps">Batch #{batchId.slice(0, 8)}</div>
      <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 mt-2 mb-2">
        {data.batch_name}
      </h1>
      <p className="text-slate-600 mb-6">Vista comparativa de {data.count} análisis del giro <strong>{analyses[0]?.business_profile?.industry}</strong>.</p>

      <div className="flex gap-2 mb-6">
        <Button onClick={downloadBatchPdf} data-testid="batch-download-pdf" className="bg-blue-900 hover:bg-blue-800">
          <Download size={14} className="mr-2" /> PDF consolidado del batch
        </Button>
      </div>

      {/* Progress KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <KPI label="Análisis totales" value={data.count} />
        <KPI label="Completados" value={completed.length} accent="text-green-700" />
        <KPI label="En proceso" value={running} accent={running > 0 ? "text-amber-700" : ""} />
        <KPI label="Score promedio" value={avgScore != null ? avgScore.toFixed(1) : '—'} accent={avgScore != null ? "" : "text-slate-400"} />
      </div>

      {running > 0 && (
        <div className="bg-blue-50 border border-blue-200 p-4 mb-6 flex items-center gap-3">
          <Loader2 className="animate-spin text-blue-900" size={18} />
          <span className="text-sm text-blue-900">{running} análisis en proceso. La comparación se actualiza automáticamente cada 8s.</span>
        </div>
      )}

      <Tabs defaultValue="ranking">
        <TabsList className="mb-4 bg-white border border-slate-200">
          <TabsTrigger value="ranking" data-testid="batch-tab-ranking">Ranking</TabsTrigger>
          <TabsTrigger value="matrix" data-testid="batch-tab-matrix">Matriz de índices</TabsTrigger>
          <TabsTrigger value="radar" data-testid="batch-tab-radar">Radar comparativo</TabsTrigger>
          <TabsTrigger value="map" data-testid="batch-tab-map">Mapa nacional</TabsTrigger>
          <TabsTrigger value="recommendations" data-testid="batch-tab-recs">Recomendaciones</TabsTrigger>
        </TabsList>

        {/* RANKING */}
        <TabsContent value="ranking">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            {completed.slice(0, 3).map((a, idx) => {
              const Icon = idx === 0 ? Trophy : idx === 1 ? Medal : Award;
              const bg = idx === 0 ? 'bg-yellow-50 border-yellow-500' : idx === 1 ? 'bg-slate-50 border-slate-400' : 'bg-orange-50 border-orange-500';
              return (
                <Link key={a.id} to={`/analyses/${a.id}`} data-testid={`batch-podium-${idx + 1}`} className={`block border-2 p-6 hover:shadow-lg transition-shadow ${bg}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <Icon size={22} className={idx === 0 ? 'text-yellow-600' : idx === 1 ? 'text-slate-500' : 'text-orange-600'} />
                    <span className="label-caps">Posición #{idx + 1}</span>
                  </div>
                  <div className="font-display text-6xl font-black" style={{ color: scoreColor(a.score_general) }}>{a.score_general.toFixed(0)}</div>
                  <div className="font-bold text-lg text-slate-900 mt-2">{a.location.municipality_names[0]}</div>
                  <div className="text-sm text-slate-600">{a.score_category}</div>
                  <div className="text-xs text-slate-500 mt-2">{a.recommendation?.decision}</div>
                </Link>
              );
            })}
          </div>

          <div className="bg-white border border-slate-200 p-4">
            <div className="label-caps mb-3">Distribución de score</div>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={barData}>
                <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={70} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                <ReTooltip />
                <Bar dataKey="score" radius={[4, 4, 0, 0]}>
                  {barData.map((d, i) => (<rect key={i} fill={scoreColor(d.score)} />))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white border border-slate-200 mt-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left border-b border-slate-200">
                  <th className="p-3 label-caps">#</th>
                  <th className="p-3 label-caps">Municipio</th>
                  <th className="p-3 label-caps">Score</th>
                  <th className="p-3 label-caps">Categoría</th>
                  <th className="p-3 label-caps">Recomendación</th>
                  <th className="p-3 label-caps">Estado</th>
                  <th className="p-3 label-caps text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {analyses.map((a, i) => (
                  <tr key={a.id} data-testid={`batch-rank-row-${i}`} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="p-3 font-bold text-slate-700">{i + 1}</td>
                    <td className="p-3 font-semibold">{a.location.municipality_names[0]}<div className="text-xs text-slate-500">{a.location.state_name}</div></td>
                    <td className="p-3 font-black text-2xl" style={{ color: scoreColor(a.score_general) }}>{a.score_general != null ? a.score_general.toFixed(0) : '—'}</td>
                    <td className="p-3 text-slate-700 text-xs">{a.score_category || '—'}</td>
                    <td className="p-3 text-slate-700 text-xs">{a.recommendation?.decision || '—'}</td>
                    <td className="p-3"><span className="text-xs uppercase tracking-widest font-semibold px-2 py-0.5 bg-slate-100">{a.status}</span></td>
                    <td className="p-3 text-right">
                      <Link to={`/analyses/${a.id}`} className="text-blue-900 hover:underline text-xs inline-flex items-center gap-1">
                        Abrir <ExternalLink size={12} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* MATRIX */}
        <TabsContent value="matrix">
          <div className="bg-white border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="p-3 label-caps text-left sticky left-0 bg-slate-50 z-10">Índice</th>
                  {completed.map((a) => (
                    <th key={a.id} className="p-3 label-caps text-center min-w-[100px]">
                      <div className="font-bold text-slate-900 normal-case tracking-normal text-xs">{a.location.municipality_names[0]}</div>
                    </th>
                  ))}
                </tr>
                <tr className="bg-slate-100 border-b border-slate-200">
                  <td className="p-2 font-bold sticky left-0 bg-slate-100 z-10">Score general</td>
                  {completed.map((a) => (
                    <td key={a.id} className="p-2 text-center font-black text-2xl" style={{ color: scoreColor(a.score_general) }}>{a.score_general.toFixed(0)}</td>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.entries(INDEX_LABELS).map(([key, label]) => (
                  <tr key={key} data-testid={`batch-matrix-${key}`} className="border-b border-slate-100">
                    <td className="p-2 font-medium sticky left-0 bg-white z-10 border-r border-slate-100">{label}</td>
                    {completed.map((a) => {
                      const v = a.indices?.[key]?.score;
                      return (
                        <td key={a.id} className="p-2 text-center">
                          <div className="font-bold" style={{ color: scoreColor(v) }}>{v != null ? v.toFixed(0) : '—'}</div>
                          <div className="h-1 bg-slate-100 mt-1"><div className="h-full" style={{ width: `${v || 0}%`, backgroundColor: scoreColor(v) }} /></div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
                <tr className="border-b border-slate-100 bg-slate-50">
                  <td className="p-2 font-medium sticky left-0 bg-slate-50 z-10 border-r border-slate-100">Competidores directos</td>
                  {completed.map((a) => (
                    <td key={a.id} className="p-2 text-center text-slate-700">{a.establishments?.filter((e) => e.type === 'competencia_directa').length || 0}</td>
                  ))}
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="p-2 font-medium sticky left-0 bg-white z-10 border-r border-slate-100">Población</td>
                  {completed.map((a) => (
                    <td key={a.id} className="p-2 text-center text-slate-700 text-xs">{a.demographics?.aggregated?.population?.toLocaleString('es-MX') || '—'}</td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* RADAR */}
        <TabsContent value="radar">
          <div className="bg-white border border-slate-200 p-6">
            <div className="label-caps mb-3">Radar overlay — Top {top5.length} por score</div>
            <ResponsiveContainer width="100%" height={480}>
              <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="72%">
                <PolarGrid stroke="#e2e8f0" />
                <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11, fill: '#475569' }} />
                <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 9, fill: '#94a3b8' }} />
                {top5.map((a, i) => (
                  <Radar
                    key={a.id}
                    name={a.location.municipality_names[0]}
                    dataKey={a.location.municipality_names[0] || `#${i + 1}`}
                    stroke={PALETTE[i]}
                    fill={PALETTE[i]}
                    fillOpacity={0.15}
                  />
                ))}
                <Legend />
                <ReTooltip />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </TabsContent>

        {/* MAPA NACIONAL */}
        <TabsContent value="map">
          <div className="bg-white border border-slate-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="label-caps">Mapa nacional del batch</div>
              <div className="flex flex-wrap gap-3 text-xs">
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full" style={{ background: '#15803d' }} />≥ 75</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full" style={{ background: '#1e3a8a' }} />55–74</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full" style={{ background: '#b45309' }} />40–54</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full" style={{ background: '#b91c1c' }} />&lt; 40</span>
              </div>
            </div>
            <div style={{ height: 560 }} className="border border-slate-200 overflow-hidden" data-testid="batch-map-container">
              <MapContainer center={[23.6, -102.5]} zoom={5} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
                <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                {analyses.map((a, i) => {
                  const lat = a.location?.center_lat; const lon = a.location?.center_lon;
                  if (!lat || !lon) return null;
                  const rank = completed.findIndex((c) => c.id === a.id) + 1;
                  const score = a.score_general;
                  const radius = score != null ? 12 + score / 8 : 8;
                  return (
                    <CircleMarker
                      key={a.id}
                      center={[lat, lon]}
                      radius={radius}
                      pathOptions={{ color: scoreColor(score), fillColor: scoreColor(score), fillOpacity: 0.65, weight: 2 }}
                    >
                      <MapTooltip permanent direction="top" offset={[0, -radius]} className="!bg-white !border !border-slate-200 !text-slate-900 !font-semibold !px-2 !py-1 !shadow">
                        {rank > 0 ? `#${rank} · ` : ''}{score != null ? Math.round(score) : '—'}
                      </MapTooltip>
                      <Popup>
                        <strong>{a.location?.municipality_names?.[0]}</strong><br />
                        {a.location?.state_name}<br />
                        Score: <strong>{score != null ? Math.round(score) : 'Pendiente'}</strong><br />
                        {a.score_category}<br />
                        <Link to={`/analyses/${a.id}`} className="text-blue-900 font-semibold">Abrir análisis →</Link>
                      </Popup>
                    </CircleMarker>
                  );
                })}
              </MapContainer>
            </div>
            <div className="text-xs text-slate-500 mt-3">
              Tamaño del marcador proporcional al score. Municipios sin coordenadas no se muestran.
            </div>
          </div>
        </TabsContent>

        {/* RECOMENDACIONES */}
        <TabsContent value="recommendations">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {analyses.map((a) => (
              <div key={a.id} data-testid={`batch-rec-${a.id}`} className="bg-white border border-slate-200 p-5">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="label-caps">{a.location.state_name}</div>
                    <div className="font-display text-xl font-bold">{a.location.municipality_names[0]}</div>
                  </div>
                  <div className="font-display text-4xl font-black" style={{ color: scoreColor(a.score_general) }}>
                    {a.score_general != null ? a.score_general.toFixed(0) : '—'}
                  </div>
                </div>
                <div className="text-xs text-slate-500 mb-2">{a.score_category}</div>
                <div className="text-sm font-semibold text-blue-900 mb-1">{a.recommendation?.decision || 'Pendiente'}</div>
                <p className="text-xs text-slate-600 leading-relaxed">{a.recommendation?.reasoning?.slice(0, 240) || a.executive_summary?.slice(0, 240) || 'Sin recomendación aún.'}</p>
                <Link to={`/analyses/${a.id}`} className="text-xs text-blue-900 hover:underline inline-flex items-center gap-1 mt-3">
                  Ver detalle <ExternalLink size={11} />
                </Link>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

const KPI = ({ label, value, accent = '' }) => (
  <div className="bg-white border border-slate-200 p-4">
    <div className="label-caps">{label}</div>
    <div className={`font-display text-3xl font-extrabold mt-1 ${accent || 'text-slate-900'}`}>{value}</div>
  </div>
);
